import { Controller } from '@hotwired/stimulus';
import { useVisibility } from 'stimulus-use';
import { debug } from '../utils/debug';
import { getLocale, buildLocalePath } from '../utils/locale';

/**
 * Search Controller
 *
 * Unified search combining Swiftype product search with internal Publications.
 * Manages search modal, result rendering, filtering by type, and infinite scroll.
 * Uses stimulus-use visibility to coordinate multiple search forms.
 *
 * @controller search
 * @target {HTMLInputElement} input - Search input field
 * @target {HTMLElement} results - Container for search results
 * @target {HTMLElement} panel - Modal/panel container (or #searchModal)
 * @target {HTMLElement} filters - Filter buttons container
 * @value {String} selectedType - Current filter type (All, Products, Publications)
 * @value {Boolean} inline - Whether this is an inline search form
 * @value {Boolean} autosubmit - Auto-submit on input change
 *
 * @action submit - Trigger search (form submit or Enter key)
 * @action filter - Filter results by type
 * @action loadPage - Load next page of results (infinite scroll)
 * @action clearSearchState - Reset search state
 *
 * @example Navbar search form
 * <form data-controller="search" data-action="submit->search#submit">
 *   <input type="search"
 *          data-search-target="input"
 *          placeholder="Search products...">
 * </form>
 *
 * <!-- Modal defined separately (moved to body) -->
 * <div id="searchModal" class="modal">
 *   <div data-search-target="filters">
 *     <button data-action="click->search#filter">All</button>
 *     <button data-action="click->search#filter">Products</button>
 *   </div>
 *   <div data-search-target="results"></div>
 * </div>
 *
 * @notes
 * - Coordinates multiple search forms via window.__wySearchPrimary
 * - Uses visibility detection to determine "primary" search form
 * - Interleaves Swiftype results with internal Publications
 * - Supports category filtering with result counts
 *
 * @used-in app/views/www/navbar/_search_form.html.erb
 * @used-in app/views/www/shared/_search_modal.html.erb
 */
export default class extends Controller {
  static targets = ['input', 'results', 'panel', 'filters'];
  static values = { selectedType: String, inline: Boolean, autosubmit: Boolean };

  connect() {
    // Use visibility composable to determine if this search form is visible
    useVisibility(this, {
      threshold: 0.1,
      callback: (entry) => this.visibilityChanged(entry)
    });

    // Use modal only
    this.panelEl = this.hasPanelTarget ? this.panelTarget : document.getElementById('searchModal');
    if (this.panelEl && this.panelEl.parentElement !== document.body) {
      document.body.appendChild(this.panelEl);
    }
    this.resultsEl = this.panelEl ? this.panelEl.querySelector('[data-search-target="results"]') : null;
    this.filtersEl = this.panelEl ? this.panelEl.querySelector('[data-search-target="filters"]') : null;
    // Reset paging state
    this.queryTerm = '';
    this.page = 1;
    this.hasMore = true;
    this.isLoading = false;
    this.allResults = [];
    this.categoryCounts = {};

    // Initialize as non-primary - visibility callback will set primary status
    this.isPrimary = false;
    this.isVisible = false;

    // Filter delegation will be set up when becoming primary

    // Ensure at least one search form is primary
    this.ensurePrimary();
  }

  ensurePrimary() {
    // If no primary exists, make this one primary
    if (!window.__wySearchPrimary) {
      debug('search', 'ensuring primary - no existing primary', { element: this.element });
      this.setAsPrimary();
    } else {
      debug('search', 'primary already exists', {
        existing: window.__wySearchPrimary.element,
        current: this.element
      });
    }
  }

  setAsPrimary() {
    // Clear any existing primary
    if (window.__wySearchPrimary) {
      window.__wySearchPrimary.isPrimary = false;
    }

    // Set this as primary
    window.__wySearchPrimary = this;
    this.isPrimary = true;
    this.bindModalListeners();
    this.bindFiltersDelegation();

    debug('search', 'set as primary', { element: this.element });
  }

  visibilityChanged(entry) {
    const wasVisible = this.isVisible;
    this.isVisible = entry.isIntersecting;

    debug('search', 'visibility changed', {
      isVisible: this.isVisible,
      wasVisible,
      element: this.element,
      isPrimary: this.isPrimary
    });

    // Only the visible search form should be primary
    if (this.isVisible && !this.isPrimary) {
      this.setAsPrimary();
    } else if (!this.isVisible && this.isPrimary) {
      // Clear primary status when not visible
      this.isPrimary = false;
      if (window.__wySearchPrimary === this) {
        window.__wySearchPrimary = null;
      }

      debug('search', 'lost primary status', { element: this.element });
    }

    // Auto-run query if requested (e.g., 404 page) and now visible
    if (this.isPrimary && this.autosubmitValue && this.hasInputTarget) {
      const val = (this.inputTarget.value || '').trim();
      if (val) setTimeout(() => this.submit(new Event('submit')), 0);
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.submit(event);
    }
  }

  async submit(event) {
    event.preventDefault();
    debug('search', 'submit called', {
      isPrimary: this.isPrimary,
      isVisible: this.isVisible,
      element: this.element,
      hasPrimary: !!window.__wySearchPrimary
    });

    // If this isn't primary but there is a primary, forward to it
    if (!this.isPrimary && window.__wySearchPrimary && window.__wySearchPrimary !== this) {
      debug('search', 'forwarding to primary', { primary: window.__wySearchPrimary.element });
      const primary = window.__wySearchPrimary;
      primary.inputTarget.value = this.inputTarget.value;
      return primary.submit(event);
    }

    // If no primary exists, make this one primary
    if (!this.isPrimary) {
      debug('search', 'no primary exists, making this primary');
      this.setAsPrimary();
    }

    const term = (this.inputTarget.value || '').trim();
    if (!term) return;

    // Debounce rapid submissions
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
    }

    this.submitTimeout = setTimeout(async () => {
      // Store the search term before clearing state
      const searchTerm = term;

      // Clear any existing search state before starting new search
      this.clearSearchState();

      // Initialize new search
      this.queryTerm = searchTerm;
      this.page = 1;
      this.hasMore = true;
      this.isLoading = false;
      this.allResults = [];

      // Mirror navbar value into modal input before showing (non-inline only)
      if (this.panelEl && !this.inlineValue) {
        const modalInput = this.panelEl.querySelector('[data-role="modal-search-input"]');
        if (modalInput) modalInput.value = searchTerm;
      }

      // First load based on current filter
      await this.loadPage(1, { replace: true });
      if (!this.inlineValue) this.openPanel();
      this.ensureInfiniteScroll();
    }, 100); // 100ms debounce
  }

  disconnect() {
    // Clear any pending timeouts
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
      this.submitTimeout = null;
    }
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = null;
    }

    // Remove event listeners
    if (this.modalInputListener) {
      const modalInput = this.panelEl?.querySelector('[data-role="modal-search-input"]');
      modalInput?.removeEventListener('input', this.modalInputListener);
      modalInput?.removeEventListener('keydown', this.modalKeydownListener);
    }
    if (this.modalSubmitListener) {
      const modalSubmit = this.panelEl?.querySelector('[data-role="modal-search-submit"]');
      modalSubmit?.removeEventListener('click', this.modalSubmitListener);
    }

    // Remove filter listeners
    const filterContainers = [];
    if (this.hasFiltersTarget) filterContainers.push(this.filtersTarget);
    if (this.filtersEl) filterContainers.push(this.filtersEl);

    filterContainers.forEach(container => {
      if (container._filterListener) {
        container.removeEventListener('click', container._filterListener);
        delete container._filterListener;
      }
    });

    if (window.__wySearchPrimary === this) {
      try { delete window.__wySearchPrimary; } catch (_) { window.__wySearchPrimary = null; }
    }
  }

  bindModalListeners() {
    if (!this.panelEl) return;
    const modalInput = this.panelEl.querySelector('[data-role="modal-search-input"]');
    const modalSubmit = this.panelEl.querySelector('[data-role="modal-search-submit"]');

    // Remove existing listeners to prevent duplicates
    if (this.modalInputListener) {
      modalInput?.removeEventListener('input', this.modalInputListener);
      modalInput?.removeEventListener('keydown', this.modalKeydownListener);
    }
    if (this.modalSubmitListener) {
      modalSubmit?.removeEventListener('click', this.modalSubmitListener);
    }

    if (modalInput) {
      // Prefill from navbar input
      modalInput.value = this.inputTarget.value || '';

      // Keep navbar input synchronized as user types in the modal
      this.modalInputListener = () => {
        this.inputTarget.value = modalInput.value;
      };
      modalInput.addEventListener('input', this.modalInputListener);

      // Submit on Enter in modal
      this.modalKeydownListener = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.inputTarget.value = modalInput.value;
          this.submit(e);
        }
      };
      modalInput.addEventListener('keydown', this.modalKeydownListener);
    }

    if (modalSubmit) {
      this.modalSubmitListener = (e) => {
        e.preventDefault();
        const el = this.panelEl.querySelector('[data-role="modal-search-input"]');
        this.inputTarget.value = (el && el.value) || '';
        this.submit(e);
      };
      modalSubmit.addEventListener('click', this.modalSubmitListener);
    }
  }

  bindFiltersDelegation() {
    // Set up filter button delegation for both inline and modal filters
    const filterContainers = [];

    // Add inline filters if present
    if (this.hasFiltersTarget) {
      filterContainers.push(this.filtersTarget);
    }

    // Add modal filters if present
    if (this.filtersEl) {
      filterContainers.push(this.filtersEl);
    }

    // Set up delegation for each container
    filterContainers.forEach(container => {
      // Remove existing listener if any
      if (container._filterListener) {
        container.removeEventListener('click', container._filterListener);
      }

      // Create new listener
      container._filterListener = (e) => {
        const btn = e.target && e.target.closest('button[data-type]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();

        // Only handle if we're the primary controller
        if (this.isPrimary) {
          this.filter(btn);
        }
      };

      container.addEventListener('click', container._filterListener);
    });
  }

  async fetchSwiftype(q) {
    // Default to first page unless overridden via this.page during loadPage
    const engine_key = 'zuApThgaNpjV7T7TYELq';
    const locale = getLocale();
    if (!locale) {
      debug.warn('search', 'Cannot fetch Swiftype results - locale unavailable');
      return { results: [], info: {} };
    }
    const domainIdentifier = locale === 'en-CA' ? 2 : 1;
    const tag = locale === 'en-CA' ? 'en-CA' : 'en-US';

    const body = {
      engine_key,
      q,
      page: this.page || 1,
      per_page: 10,
      spelling: 'retry',
      filters: { page: { tags: tag, 'domain-identifier': domainIdentifier } }
    };

    // If a specific type is selected (except Publication which we source internally), filter on swiftype 'type'
    const type = this.selectedTypeValue || 'All';
    if (type && type !== 'All' && type !== 'Publication') {
      body.filters.page.type = type;
    }

    debug('search', 'swiftype request', body);
    const res = await fetch('https://api.swiftype.com/api/v1/public/engines/search', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    const json = await res.json();
    const pageInfo = json?.info?.page || {};
    debug('search', 'swiftype response', {
      query: body.q,
      type: body.filters?.page?.type,
      resultCount: json?.records?.page?.length,
      pageInfo,
      results: json?.records?.page?.map(r => ({ title: r.title, type: r.type, url: r.url }))
    });
    const results = (json?.records?.page || []).map((r) => ({
      id: r.id,
      url: r.url,
      image: r.image,
      highlight: r.highlight || {},
      title: r.title,
      body: r.body,
      type: r.type
    }));
    return { results, info: pageInfo };
  }

  async fetchPublications(q) {
    const locale = getLocale();
    const url = locale
      ? `/${locale}/publications/search?q=${encodeURIComponent(q)}`
      : `/publications/search?q=${encodeURIComponent(q)}`;
    debug('search', 'publications request', { url, query: q });
    const res = await fetch(url);
    const results = await res.json();
    debug('search', 'publications response', { query: q, resultCount: results.length, results: results.map(r => ({ title: r.title, type: r.type, url: r.url })) });
    // Normalize to same shape used by renderer and mark source
    return results.map((r) => ({ ...r, highlight: {}, type: r.type || 'Publication', source: 'publication' }));
  }

  zip(a = [], b = []) {
    // Interleave Swiftype results with Publications at 3:1 ratio
    const result = [];
    let aIndex = 0;
    let bIndex = 0;

    while (aIndex < a.length || bIndex < b.length) {
      // Add up to 3 Swiftype results
      for (let i = 0; i < 3 && aIndex < a.length; i++) {
        result.push(a[aIndex++]);
      }

      // Add 1 publication
      if (bIndex < b.length) {
        result.push(b[bIndex++]);
      }
    }

    debug('search', 'interleaved results', {
      swiftypeCount: a.length,
      publicationCount: b.length,
      totalResults: result.length,
      order: result.slice(0, 10).map(r => r.type || 'Publication')
    });

    return result;
  }

  renderResults(results, { append } = { append: false }) {
    // Always render inside the modal's results container
    const container = this.resultsEl || (this.hasResultsTarget ? this.resultsTarget : null);
    if (!container) {
      debug.warn('search', 'results container not found');
      return;
    }
    if (!append) container.innerHTML = '';
    // If warning notice is present and a slot exists in modal, render above input
    if (!append && window.wySearchNotice && this.panelEl) {
      const slot = this.panelEl.querySelector('#search-warning-slot');
      if (slot && slot.childElementCount === 0) {
        const note = document.createElement('div');
        note.className = 'alert alert-warning mb-2';
        note.textContent = window.wySearchNotice;
        slot.appendChild(note);
      }
      try { delete window.wySearchNotice; } catch (_) { window.wySearchNotice = null; }
    }
    if (!results.length && !append) {
      container.innerHTML = `
        <div class="text-center py-5">
          <i class="fas fa-search fa-3x text-muted mb-3 d-block"></i>
          <h4 class="text-muted">No results found</h4>
          <p class="text-muted">Try adjusting your search terms or filters</p>
        </div>
      `;
      return;
    }
    // Create or reuse results wrapper
    let wrap = container.querySelector('#st-search-results');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.id = 'st-search-results';
      wrap.className = 'mt-3';
      container.appendChild(wrap);
    }
    const fragments = [];
    results.forEach((r) => {
      const title = r.highlight?.title || r.title || '';
      const body = r.highlight?.body || r.body || '';
      const safeBody = body ? body.substring(0, 300) : '';

      // Type-specific colors
      const typeColors = {
        'FAQ': 'bg-info text-white',
        'Product': 'bg-success text-white',
        'Blog': 'bg-primary text-white',
        'Video': 'bg-danger text-white',
        'Support': 'bg-warning text-dark',
        'Publication': 'bg-secondary text-white'
      };

      const typeBadge = r.type ? `<span class="badge rounded-pill ${typeColors[r.type] || 'bg-secondary text-white'} ms-2">${r.type}</span>` : '';

      fragments.push(`
        <div class="search-result-item mb-4" id="${r.id}">
          <a href="${r.url}" class="text-decoration-none d-block">
            <div class="card border-0 shadow-sm h-100 transition-all hover-shadow">
              <div class="card-body">
                <div class="row align-items-center">
                  ${r.image ? `
                  <div class="col-auto d-none d-md-block">
                    <div class="search-result-thumb rounded overflow-hidden" style="width: 100px; height: 100px;">
                      <img class="img-fluid w-100 h-100 object-fit-cover" src="${r.image}" alt="${this.escapeHtml(r.title || '')}"/>
                    </div>
                  </div>
                  ` : ''}
                  <div class="${r.image ? 'col' : 'col-12'}">
                    <h5 class="mb-2 text-primary fw-semibold d-flex align-items-center flex-wrap">
                      <span class="me-auto">${title}</span>
                      ${typeBadge}
                    </h5>
                    ${safeBody ? `<p class="mb-0 text-muted small">...${safeBody}...</p>` : ''}
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>
      `);
    });
    wrap.insertAdjacentHTML('beforeend', fragments.join(''));
    this.ensureSentinel(container);

    // Calculate category counts
    this.calculateCategoryCounts();

    // Update modal title with count for current filter
    if (this.panelEl) {
      const titleEl = this.panelEl.querySelector('.modal-title');
      const currentType = this.selectedTypeValue || 'All';
      const count = this.categoryCounts[currentType] || 0;
      if (titleEl) titleEl.textContent = `${count} Search Results Found`;
    }
    debug('search', 'rendered results', { appended: append, total: (this.allResults || []).length });
  }

  escapeHtml(s) {
    return s.replace(/[&<>"]/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;'
    }[c]));
  }

  openPanel() {
    const el = this.panelEl;
    if (!el) {
      debug('search', 'no modal found');
      return;
    }
    const Modal = window.bootstrap && window.bootstrap.Modal;
    if (Modal) {
      const instance = Modal.getOrCreateInstance(el);
      instance.show();
      debug('search', 'modal opened');
    } else {
      el.classList.add('show');
      el.style.display = 'block';
      el.removeAttribute('aria-hidden');
      debug('search', 'modal fallback show');
    }
  }

  // UI: filters
  showFilters() {
    const el = this.filtersEl || (this.hasFiltersTarget ? this.filtersTarget : null);
    if (!el) return;
    el.classList.remove('d-none');
    // Highlight the currently selected type (default to All)
    const current = this.selectedTypeValue || 'All';
    const buttons = el.querySelectorAll('button[data-type]');
    buttons.forEach((b) => {
      b.classList.remove('btn-primary');
      b.classList.remove('selected');
      if (!b.classList.contains('btn-secondary')) b.classList.add('btn-secondary');
      if (b.getAttribute('data-type') === current) {
        b.classList.remove('btn-secondary');
        b.classList.add('btn-primary');
        b.classList.add('selected');
      }
    });
  }

  calculateCategoryCounts() {
    // Initialize counts
    this.categoryCounts = {
      'All': 0,
      'FAQ': 0,
      'Product': 0,
      'Blog': 0,
      'Video': 0,
      'Support': 0,
      'Publication': 0
    };

    // Count results by type
    (this.allResults || []).forEach(result => {
      const type = result.type || 'Publication';
      if (this.categoryCounts.hasOwnProperty(type)) {
        this.categoryCounts[type]++;
      }
      this.categoryCounts['All']++;
    });

    debug('search', 'category counts', this.categoryCounts);
  }

  filter(eventOrButton) {
    // If this isn't primary but there is a primary, forward to it
    if (!this.isPrimary && window.__wySearchPrimary && window.__wySearchPrimary !== this) {
      debug('search', 'forwarding filter to primary', { primary: window.__wySearchPrimary.element });
      return window.__wySearchPrimary.filter(eventOrButton);
    }

    // If no primary exists, make this one primary
    if (!this.isPrimary) {
      debug('search', 'no primary exists for filter, making this primary');
      this.setAsPrimary();
    }

    const raw = eventOrButton && (eventOrButton.currentTarget || eventOrButton.target || eventOrButton);
    const button = raw && raw.closest ? raw.closest('button[data-type]') : null;
    const type = (button && button.getAttribute('data-type')) || 'All';
    this.selectedTypeValue = type;

    debug('search', 'filter changed', { type, isPrimary: this.isPrimary });
    // Toggle active styles in the group
    const group = this.filtersEl || (this.hasFiltersTarget ? this.filtersTarget : null);
    if (group) {
      const buttons = group.querySelectorAll('button[data-type]');
      buttons.forEach((b) => {
        b.classList.remove('btn-primary');
        b.classList.remove('selected');
        b.classList.add('btn-secondary');
      });
      if (button) {
        button.classList.remove('btn-secondary');
        button.classList.add('btn-primary');
        button.classList.add('selected');
      }
    }
    // Only start a new search if we have a query term
    if (!this.queryTerm) {
      debug('search', 'no query term, skipping filter search');
      return;
    }

    // Cancel any pending searches
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }

    // Debounce filter changes
    this.filterTimeout = setTimeout(() => {
      // Start a new search for the selected type
      this.page = 1;
      this.hasMore = true;
      this.isLoading = false;

      // Update the counter immediately based on cached counts
      if (this.categoryCounts && this.panelEl) {
        const titleEl = this.panelEl.querySelector('.modal-title');
        const count = this.categoryCounts[type] || 0;
        if (titleEl) titleEl.textContent = `${count} Search Results Found`;
      }

      this.clearResults();
      this.loadPage(1, { replace: true }).then(() => this.ensureInfiniteScroll());
    }, 50); // Short debounce for filters
  }

  applyFilter(results = []) {
    const t = (this.selectedTypeValue || 'All');
    if (t === 'All') return results;
    return results.filter((r) => (r.type || '').toLowerCase() === t.toLowerCase());
  }

  normalizeType(r) {
    if (!r) return r;
    // Map Swiftype or backend provided types into our canonical set used by the UI
    const map = {
      'faqs': 'FAQ',
      'faq': 'FAQ',
      'product': 'Product',
      'products': 'Product',
      'blog': 'Blog',
      'post': 'Blog',
      'video': 'Video',
      'videos': 'Video',
      'support': 'Support',
      'publication': 'Publication',
      'publications': 'Publication'
    };
    const raw = (r.type || '').toString();
    const key = raw.toLowerCase();
    const canon = map[key] || (raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '');

    // Only default to 'Publication' if this is actually from the publications API
    // For Swiftype results with empty type, leave it empty
    const finalType = canon || (r.source === 'publication' ? 'Publication' : '');

    return { ...r, type: finalType };
  }

  // Paging helpers
  async loadPage(page, { replace } = { replace: false }) {
    if (this.isLoading || !this.hasMore) {
      debug('search', 'loadPage skipped', {
        isLoading: this.isLoading,
        hasMore: this.hasMore,
        page
      });
      return;
    }
    this.isLoading = true;

    // Show loading state for first page
    if (page === 1 && replace) {
      const container = this.resultsEl || (this.hasResultsTarget ? this.resultsTarget : null);
      if (container) {
        container.innerHTML = `
          <div class="search-loading">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Searching...</p>
          </div>
        `;
      }
    }
    this.page = page;
    const type = this.selectedTypeValue || 'All';
    const t0 = performance.now();
    let results = [];
    debug('search', 'loadPage called', { page, type, queryTerm: this.queryTerm, isPrimary: this.isPrimary });
    try {
      if (type === 'All') {
        if (page === 1) {
          const [st, pubs] = await Promise.all([
            this.fetchSwiftype(this.queryTerm).catch(() => ({ results: [], info: {} })),
            this.fetchPublications(this.queryTerm).catch(() => [])
          ]);
          results = this.zip(st.results, pubs).map((r) => this.normalizeType(r));
          this.swiftypeTotal = Number(st.info?.total_result_count || 0);
          this.totalAvailable = this.swiftypeTotal + (pubs.length || 0);
        } else {
          const st = await this.fetchSwiftype(this.queryTerm).catch(() => ({ results: [], info: {} }));
          results = st.results.map((r) => this.normalizeType(r));
        }
      } else if (type === 'Publication') {
        if (page === 1) {
          const pubs = await this.fetchPublications(this.queryTerm).catch(() => []);
          results = pubs.map((r) => this.normalizeType(r));
          this.totalAvailable = pubs.length || 0;
        } else {
          results = []; // no pagination for publications endpoint
        }
      } else {
        const st = await this.fetchSwiftype(this.queryTerm).catch(() => ({ results: [], info: {} }));
        results = st.results.map((r) => this.normalizeType(r));
        if (page === 1) this.totalAvailable = Number(st.info?.total_result_count || 0);
      }
    } finally {
      const t1 = performance.now();
      debug('search', 'page loaded', { page: this.page, type, count: results.length, ms: Math.round(t1 - t0) });
      this.isLoading = false;
    }

    if (results.length === 0) {
      this.hasMore = false;
      // Still need to render to show "no results" message and hide spinner
      if (replace) {
        this.showFilters();
        this.renderResults([], { append: false });
      }
      return;
    }
    this.allResults = (this.allResults || []).concat(results);
    this.showFilters();
    this.renderResults(results, { append: !replace });
  }

  ensureInfiniteScroll() {
    if (this.io) return; // already set up
    const container = this.resultsEl || (this.hasResultsTarget ? this.resultsTarget : null);
    if (!container) return;
    this.ensureSentinel(container);
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading && this.hasMore) {
          this.loadPage(this.page + 1, { replace: false });
        }
      });
    }, { root: container.closest('.modal-body') || container, threshold: 0.1 });
    if (this.sentinelEl) this.io.observe(this.sentinelEl);
  }

  ensureSentinel(container) {
    let sentinel = container.querySelector('#search-infinite-sentinel');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.id = 'search-infinite-sentinel';
      sentinel.className = 'py-2';
      container.appendChild(sentinel);
    }
    this.sentinelEl = sentinel;
  }

  clearResults() {
    const container = this.resultsEl || (this.hasResultsTarget ? this.resultsTarget : null);
    if (container) container.innerHTML = '';
    if (this.io && this.sentinelEl) {
      try {
        this.io.unobserve(this.sentinelEl);
      } catch (e) {
        // ignore
      }
      this.io.disconnect();
      this.io = null;
    }
  }

  clearSearchState() {
    debug('search', 'clearing search state', {
      oldQueryTerm: this.queryTerm,
      oldPage: this.page,
      oldSelectedType: this.selectedTypeValue
    });

    // Reset all search state
    this.queryTerm = '';
    this.page = 1;
    this.hasMore = true;
    this.isLoading = false;
    this.allResults = [];
    this.swiftypeTotal = 0;
    this.totalAvailable = 0;

    // Clear results display
    this.clearResults();

    debug('search', 'search state cleared');
  }
}
